import { Box, Stack, Tooltip, useTheme } from "@mui/material";
import { ManifestComplianceEscalation, SubmissionStatus } from "generated-graphql/graphql";
import { prettyPrintDaysFromInt } from "util/dates";

import {
  EManifestComplianceInput,
  getEManifestComplianceDisplay,
} from "./eManifestComplianceDisplay";

export interface EManifestCellInput extends EManifestComplianceInput {
  isEManifest?: boolean | null;
  isUniformHazardousManifest?: boolean | null;
  daysSinceShipment?: number | null;
}

export interface EManifestCellProps {
  input: EManifestCellInput;
}

/**
 * Short, concise compliance-action label folded in from the retired Status
 * column. Returns null below the 45-day threshold (the day count alone tells
 * the story until escalation kicks in).
 */
function actionStateLabel(input: EManifestCellInput): string | null {
  const escalation = input.eManifestComplianceEscalation;
  if (escalation === ManifestComplianceEscalation.Overdue_60) {
    if (input.exceptionReportSubmission?.status === SubmissionStatus.FilingComplete) {
      return "Exception filed";
    }
    if (input.exceptionReportSubmission) return "Exception started";
    return "Exception required";
  }
  if (escalation === ManifestComplianceEscalation.Overdue_45) {
    return input.transporterContactedAt ? "Transporter contacted" : "Contact transporter";
  }
  return null;
}

/**
 * The single e-Manifest compliance column. Renders only for uniform-hazardous
 * manifests still awaiting their e-Manifest record: the awaiting-days countdown
 * with the 45/60-day color scale (orange at 45, red at 60), plus the current
 * compliance action state. Empty once an e-Manifest is on file.
 *
 * Replaces the overloaded "Timeline" column and the standalone "Days awaiting
 * e-Manifest" column, and absorbs the compliance states the retired "Status"
 * column used to surface. The grid column sorts/filters on the raw
 * `daysSinceShipment` ordinal; the "X days" text here is just its presentation.
 */
export function EManifestCell({ input }: EManifestCellProps) {
  const theme = useTheme();

  // Only manifests that actually require an e-Manifest and don't yet have one
  // show a countdown; everything else leaves the column empty.
  const awaiting = input.isUniformHazardousManifest === true && !input.isEManifest;
  if (!awaiting || input.daysSinceShipment == null) {
    return null;
  }

  const compliance = getEManifestComplianceDisplay(input);
  const severityColor: Record<string, string | undefined> = {
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
  };
  // Soften once the required action has been taken so a handled row doesn't
  // shout as loud as a fresh, un-actioned one.
  const daysColor = compliance.actionTaken
    ? theme.palette.text.secondary
    : (severityColor[compliance.severity] ?? theme.palette.text.primary);
  const stateLabel = actionStateLabel(input);

  return (
    <Tooltip title={compliance.tooltipText} placement="right" enterDelay={400}>
      <Stack
        direction="column"
        spacing={0.25}
        sx={{ minWidth: 0, width: "100%", justifyContent: "center" }}
      >
        <Box sx={{ fontSize: 13, lineHeight: 1.2, color: daysColor }}>
          {prettyPrintDaysFromInt(input.daysSinceShipment)}
        </Box>
        {stateLabel && (
          <Box sx={{ fontSize: 11, lineHeight: 1.2, color: theme.palette.text.secondary }}>
            {stateLabel}
          </Box>
        )}
      </Stack>
    </Tooltip>
  );
}
